import * as React from "react";
import { BehaviorSubject } from "rxjs";

// TODO: Add helpers that we can use in class-based components

// We use a numeric enum so we can easily compare sizes, e.g `if (currentSize < ScreenSize.LG) {...}`
/**
 * @deprecated Legacy antd/Bootstrap scale (576/768/992/1200/1600). New code should use the layout
 * tiers below — {@link useLayoutTier} and its `useIs*` helpers for behaviour, `theme.breakpoints`
 * (see styles/mq.ts) or the `media-*` SCSS mixins for styling.
 */
export enum ScreenSize {
  XS,
  SM,
  MD,
  LG,
  XL,
  XXL,
}

interface ResponsivenessSvc {
  readonly screenSize: BehaviorSubject<ScreenSize>;
}

class InvalidResponsivenessSvc implements ResponsivenessSvc {
  get screenSize(): BehaviorSubject<ScreenSize> {
    throw new Error("Accessing ResponsivenessSvc without a provider.");
  }
}

const ResponsivenessSvcContext = React.createContext<ResponsivenessSvc>(
  new InvalidResponsivenessSvc(),
);

function useObservable<T>(o: BehaviorSubject<T>) {
  const [value, setValue] = React.useState(o.getValue());
  React.useEffect(() => {
    const subscription = o.subscribe((nextValue) => {
      setValue(nextValue);
    });
    return () => subscription.unsubscribe();
  }, [o]);
  return value;
}

export function useResponsiveScreenSize(): ScreenSize {
  const svc = React.useContext(ResponsivenessSvcContext);
  return useObservable(svc.screenSize);
}

/**
 * Layout tiers — six pattern-decision boundaries shared with media_queries.scss.
 * See docs/coding-standards.md for what each tier represents.
 */
export type LayoutTier = "mobile" | "tablet" | "laptop-sm" | "laptop" | "laptop-lg" | "desktop";

export const TABLET_MIN_PX = 768;
export const LAPTOP_SM_MIN_PX = 1024;
export const LAPTOP_MIN_PX = 1280;
export const LAPTOP_LG_MIN_PX = 1440;
export const DESKTOP_MIN_PX = 1920;

export function calculateLayoutTier(screenWidth: number): LayoutTier {
  if (screenWidth >= DESKTOP_MIN_PX) {
    return "desktop";
  } else if (screenWidth >= LAPTOP_LG_MIN_PX) {
    return "laptop-lg";
  } else if (screenWidth >= LAPTOP_MIN_PX) {
    return "laptop";
  } else if (screenWidth >= LAPTOP_SM_MIN_PX) {
    return "laptop-sm";
  } else if (screenWidth >= TABLET_MIN_PX) {
    return "tablet";
  } else {
    return "mobile";
  }
}

let layoutTierSubject: BehaviorSubject<LayoutTier> | null = null;

// Lazily create a single shared subject + resize listener, no matter how many
// components call useLayoutTier() — avoids one resize listener per consumer.
function getLayoutTierSubject(): BehaviorSubject<LayoutTier> {
  if (!layoutTierSubject) {
    const subject = new BehaviorSubject<LayoutTier>(calculateLayoutTier(window.innerWidth));
    window.addEventListener("resize", () => subject.next(calculateLayoutTier(window.innerWidth)), {
      passive: true,
    });
    layoutTierSubject = subject;
  }
  return layoutTierSubject;
}

export function useLayoutTier(): LayoutTier {
  return useObservable(getLayoutTierSubject());
}

/** width < 1024 → top nav bar (hamburger menu, no side nav) */
export function useIsMobileOrTablet(): boolean {
  const tier = useLayoutTier();
  return tier === "mobile" || tier === "tablet";
}

/** width >= 1024 → side nav appears (collapsible until pinned); still drawer filters until laptop-lg */
export function useIsLaptopSmUp(): boolean {
  const tier = useLayoutTier();
  return tier !== "mobile" && tier !== "tablet";
}

/** width >= 1280 → laptop tier or above (MacBook Air / Windows QHD baseline) */
export function useIsLaptopUp(): boolean {
  const tier = useLayoutTier();
  return tier === "laptop" || tier === "laptop-lg" || tier === "desktop";
}

/** width >= 1440 → inline filters + persistent side nav by default */
export function useIsLaptopLgUp(): boolean {
  const tier = useLayoutTier();
  return tier === "laptop-lg" || tier === "desktop";
}

/** width >= 1920 → secondary panels can persist */
export function useIsDesktop(): boolean {
  const tier = useLayoutTier();
  return tier === "desktop";
}

class ResponsivenessSvcImpl implements ResponsivenessSvc {
  screenSize: BehaviorSubject<ScreenSize>;
  constructor() {
    this.screenSize = new BehaviorSubject<ScreenSize>(this.calculateSize());
  }

  mount() {
    window.addEventListener("resize", this.handleResize, { passive: true });
  }
  unmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  private calculateSize() {
    /**
     * Breakpoints are based on antd's breakpoints (which match Bootstrap 4)
     */
    const screenWidth = window.innerWidth;
    if (screenWidth >= 1600) {
      return ScreenSize.XXL;
    } else if (screenWidth >= 1200) {
      return ScreenSize.XL;
    } else if (screenWidth >= 992) {
      return ScreenSize.LG;
    } else if (screenWidth >= 768) {
      return ScreenSize.MD;
    } else if (screenWidth >= 576) {
      return ScreenSize.SM;
    } else {
      return ScreenSize.XS;
    }
  }

  private handleResize = (_e: UIEvent) => {
    this.screenSize.next(this.calculateSize());
  };
}

export function ResponsivenessSvcProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const svc = React.useMemo(() => {
    return new ResponsivenessSvcImpl();
  }, []);

  React.useEffect(() => {
    svc.mount();
    return () => {
      svc.unmount();
    };
  }, [svc]);

  return (
    <ResponsivenessSvcContext.Provider value={svc}>{children}</ResponsivenessSvcContext.Provider>
  );
}
